<script setup lang="ts">
  import HeaderBarButton from '@/common/components/headerbar/HeaderBarButton.vue'
  import QueryHeaderBarIncidents from './graphql/QueryHeaderBarIncidents.gql'
  import { useQuery } from '@vue/apollo-composable'
  import { computed } from 'vue'
  import { HeaderBarOpenIncidentsQuery, IncidentSeverity, IncidentStatus } from '@/common/graphql/types'
  import { useStore } from '@/store'

  const store = useStore()

  const hasIncidentPermission = computed(() => store.getters['user/can']('analytics::manage_incidents'))

  const { result, loading } = useQuery<HeaderBarOpenIncidentsQuery>(QueryHeaderBarIncidents, {}, {
    pollInterval: 30_000,
    fetchPolicy: 'cache-and-network',
    enabled: hasIncidentPermission,
  })

  const incidents = computed(() => result.value?.incidents ?? [])
  const severity = computed(() => {
    const critical = incidents.value.some(incident => incident.severity === IncidentSeverity.Critical && incident.status === IncidentStatus.New)
    if (critical) {
      return IncidentSeverity.Critical
    }

    const warning = incidents.value.some(incident => incident.severity === IncidentSeverity.Warning && incident.status === IncidentStatus.New)
    if (warning) {
      return IncidentSeverity.Warning
    }

    return IncidentSeverity.Info
  })

  const classes = computed(() => {
    switch (severity.value) {
    case IncidentSeverity.Critical:
      return 'border-red-500/60 bg-red-100/5'
    case IncidentSeverity.Warning:
      return 'border-yellow-500/60 bg-yellow-100/5'
    default:
      return 'border-transparent'
    }
  })
</script>

<template>
  <transition name="fade">
    <HeaderBarButton
      v-if="!loading"
      class="border"
      :class="classes"
      @click.prevent="$router.push({name: 'incidentsList'})"
    >
      {{ $t('incident.problems', { count: incidents.length }) }}
      <template #left>
        <div
          class="rounded text-3xs font-semibold px-1.5 py-1 leading-none flex items-center justify-center"
          :class="{
            'bg-yellow-500/50': severity === IncidentSeverity.Warning,
            'bg-red-500/50': severity === IncidentSeverity.Critical,
            'bg-white/10': severity === IncidentSeverity.Info,
          }"
        >
          {{ incidents.length }}
        </div>
      </template>
    </HeaderBarButton>
  </transition>
</template>